import type * as React from "react";

import { cn } from "@/lib/utils";

// MUI's plain `Table`, `TableHead`, `TableBody`, `TableRow` and `TableCell`, at
// MUI 9.2.0's defaults — what the spec details page's groups draw their lines in
// (`GroupRow.tsx`, `LineDetailsRow.tsx`, #695), and the Editor after it. The
// donor theme overrides none of them.
//
// **Not `mui-table.tsx`**, which ports the Material React Table cells the list
// screens draw: 14px of padding, MRT's stacking and its resize guides. These are
// MUI's own (`TableCell/TableCell.js:48-65`):
//
//   - every cell: `body2` (0.875rem / 1.43 / 400), `display: table-cell`,
//     `vertical-align: inherit`, left-aligned, 16px of padding, and a 1px bottom
//     rule of `lighten(alpha(divider, 1), 0.88)` — `rgba(224, 224, 224, 1)`;
//   - a head cell: `text.primary`, `line-height: 1.5rem`, weight 500;
//   - a body cell: `text.primary`;
//   - `size='small'`: `6px 16px`;
//   - the table: `width: 100%`, collapsed borders, no spacing; a row: inherit
//     colour, `vertical-align: middle`.

export function MuiBasicTable({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <table
      data-slot="mui-basic-table"
      className={cn("w-full border-collapse border-spacing-0", className)}
      {...props}
    />
  );
}

export function MuiBasicTableHead(props: React.ComponentProps<"thead">) {
  return <thead data-slot="mui-basic-table-head" {...props} />;
}

export function MuiBasicTableBody(props: React.ComponentProps<"tbody">) {
  return <tbody data-slot="mui-basic-table-body" {...props} />;
}

interface MuiBasicTableRowProps extends React.ComponentProps<"tr"> {
  /**
   * MUI's `TableRow selected` — `alpha(primary.main, action.selectedOpacity)`
   * (`TableRow.js:39-44`), the donor's navy at 8%: the Create Visualization
   * dialog's ticked option rows (#755). Under the pointer it deepens to 12%,
   * selected plus hover opacity — `.Mui-selected`'s own `&:hover`, which holds
   * without the `hover` prop (`TableRow.js:55-60`).
   */
  selected?: boolean;
}

export function MuiBasicTableRow({ selected = false, className, ...props }: MuiBasicTableRowProps) {
  return (
    <tr
      data-slot="mui-basic-table-row"
      data-selected={selected || undefined}
      className={cn(
        "align-middle text-inherit outline-0",
        selected && "bg-lennar-primary-selected hover:bg-lennar-primary-selected-hover",
        className,
      )}
      {...props}
    />
  );
}

interface MuiBasicTableCellProps extends Omit<React.ComponentProps<"td">, "align"> {
  /** A `<th>` in the head, as MUI's `TableCell` is inside a `TableHead`. */
  head?: boolean;
  size?: "medium" | "small";
  align?: "left" | "center";
}

export function MuiBasicTableCell({
  head = false,
  size = "medium",
  align = "left",
  className,
  ...props
}: MuiBasicTableCellProps) {
  const Cell = head ? "th" : "td";
  return (
    <Cell
      data-slot="mui-basic-table-cell"
      className={cn(
        "border-lennar-table-rule text-lennar-ink table-cell border-b border-solid align-[inherit] text-sm",
        head ? "leading-6 font-medium" : "leading-[1.43] font-normal",
        size === "small" ? "px-4 py-1.5" : "p-4",
        align === "center" ? "text-center" : "text-left",
        className,
      )}
      {...props}
    />
  );
}
